// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The 409 experience (design doc §10.4).
 *
 * What this dialog deliberately does **not** offer is a retry button. §10.4 rules it out,
 * and the reason is not caution for its own sake: the operator filled in a form describing
 * a machine that has since changed. Replaying their lines against the new version would
 * apply a decision to a situation they never saw — swapping a part that another person has
 * already swapped, or removing one that is no longer there.
 *
 * So the form's contents are kept, both versions are named, and the only ways forward are
 * to look at the current state or to reload and decide again.
 */

import { useTranslation } from "react-i18next";
import { Modal } from "./Modal";

interface Props {
  baseVersion: number;
  latestVersion: number;
  archiveHref: string;
  onRefresh: () => void;
  onClose: () => void;
}

export function ConflictDialog({
  baseVersion,
  latestVersion,
  archiveHref,
  onRefresh,
  onClose,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      title={t("conflict.title")}
      onClose={onClose}
      footer={
        <>
          {/* A new tab, so the half-filled form behind this dialog survives the look. */}
          <a className="link-button" href={archiveHref} target="_blank" rel="noreferrer">
            {t("conflict.openLatest")}
          </a>
          <button type="button" className="button-primary" onClick={onRefresh}>
            {t("conflict.refresh")}
          </button>
          <button type="button" onClick={onClose}>
            {t("common.close")}
          </button>
        </>
      }
    >
      <p>{t("conflict.body", { base: baseVersion, latest: latestVersion })}</p>
    </Modal>
  );
}
